import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ticket,
  Search,
  Loader2,
  ChevronRight,
  Clock,
  MessageSquare,
  CheckCircle,
  Lock,
  AlertTriangle,
  BarChart3,
  Building2,
  User,
} from "lucide-react";
import apiClient from "@/lib/api-client";

interface SupportTicketAdmin {
  id: string;
  ticketNumber: number;
  subject: string;
  category: string;
  priority: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  user: { firstName: string; lastName: string; email: string };
  organization: { name: string };
  messages: { createdAt: string; senderType: string; message: string }[];
}

interface TicketStats {
  open: number;
  inProgress: number;
  waitingReply: number;
  resolved: number;
  closed: number;
  total: number;
}

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; icon: React.ReactNode }> = {
  open: { label: "Abierto", bg: "bg-blue-100", text: "text-blue-700", icon: <Clock className="w-4 h-4" /> },
  in_progress: { label: "En progreso", bg: "bg-yellow-100", text: "text-yellow-700", icon: <Loader2 className="w-4 h-4" /> },
  waiting_reply: { label: "Esperando respuesta", bg: "bg-purple-100", text: "text-purple-700", icon: <MessageSquare className="w-4 h-4" /> },
  resolved: { label: "Resuelto", bg: "bg-green-100", text: "text-green-700", icon: <CheckCircle className="w-4 h-4" /> },
  closed: { label: "Cerrado", bg: "bg-gray-100", text: "text-gray-700", icon: <Lock className="w-4 h-4" /> },
};

const CATEGORY_LABELS: Record<string, string> = {
  general: "General",
  billing: "Facturación",
  technical: "Técnico",
  feature_request: "Mejora",
};

const PRIORITY_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  low: { label: "Baja", bg: "bg-gray-100", text: "text-gray-600" },
  normal: { label: "Normal", bg: "bg-blue-100", text: "text-blue-600" },
  high: { label: "Alta", bg: "bg-orange-100", text: "text-orange-600" },
  urgent: { label: "Urgente", bg: "bg-red-100", text: "text-red-600" },
};

export default function AdminTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<SupportTicketAdmin[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [statusFilter]);

  const fetchStats = async () => {
    try {
      const response = await apiClient.get("/support-tickets/stats?all=true");
      setStats(response.data.data);
    } catch {
      console.error("Error fetching stats");
    }
  };

  const fetchTickets = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({ limit: "100" });
      if (statusFilter !== "all") params.append("status", statusFilter);
      params.append("all", "true");
      const response = await apiClient.get(`/support-tickets?${params}`);
      setTickets(response.data.data);
    } catch {
      console.error("Error fetching tickets");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredTickets = searchTerm
    ? tickets.filter(
        (t) =>
          t.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.ticketNumber.toString().includes(searchTerm) ||
          t.organization.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          `${t.user.firstName} ${t.user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : tickets;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
          Tickets de Soporte
        </h1>
        <p className="text-gray-500 text-sm">Gestión de consultas y problemas de los clientes</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-500">Total</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-blue-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-blue-500" />
              <span className="text-xs font-medium text-blue-600">Abiertos</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">{stats.open}</p>
          </div>
          <div className="bg-white rounded-xl border border-yellow-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-yellow-500" />
              <span className="text-xs font-medium text-yellow-600">En progreso</span>
            </div>
            <p className="text-2xl font-bold text-yellow-700">{stats.inProgress}</p>
          </div>
          <div className="bg-white rounded-xl border border-purple-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="w-4 h-4 text-purple-500" />
              <span className="text-xs font-medium text-purple-600">Esperando</span>
            </div>
            <p className="text-2xl font-bold text-purple-700">{stats.waitingReply}</p>
          </div>
          <div className="bg-white rounded-xl border border-green-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-xs font-medium text-green-600">Resueltos</span>
            </div>
            <p className="text-2xl font-bold text-green-700">{stats.resolved}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Lock className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-500">Cerrados</span>
            </div>
            <p className="text-2xl font-bold text-gray-600">{stats.closed}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por asunto, #ticket, organización o usuario..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Status filter */}
          <div className="flex flex-wrap gap-1.5">
            {[
              { value: "all", label: "Todos" },
              { value: "open", label: "Abiertos" },
              { value: "in_progress", label: "En progreso" },
              { value: "waiting_reply", label: "Esperando" },
              { value: "resolved", label: "Resueltos" },
              { value: "closed", label: "Cerrados" },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  statusFilter === opt.value
                    ? "bg-primary-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Ticket List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : filteredTickets.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Ticket className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay tickets</h3>
          <p className="text-gray-500">No se encontraron tickets con los filtros seleccionados.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Mobile card view */}
          <div className="md:hidden divide-y divide-gray-100">
            {filteredTickets.map((ticket) => {
              const statusInfo = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
              const priorityInfo = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.normal;
              const lastMessage = ticket.messages?.[0];
              return (
                <button
                  key={ticket.id}
                  onClick={() => navigate(`/help/ticket/${ticket.id}`)}
                  className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-mono text-gray-400">#{ticket.ticketNumber}</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}>
                      {statusInfo.label}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityInfo.bg} ${priorityInfo.text}`}>
                      {priorityInfo.label}
                    </span>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm truncate">{ticket.subject}</h4>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    <Building2 className="w-3 h-3" />
                    <span className="truncate">{ticket.organization.name}</span>
                    <span>·</span>
                    <span className="truncate">{ticket.user.firstName}</span>
                  </div>
                  {lastMessage && (
                    <p className="text-xs text-gray-400 mt-1 line-clamp-1">
                      {lastMessage.senderType === "admin" ? "Tú: " : "Cliente: "}
                      {lastMessage.message}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">{formatDate(ticket.updatedAt)}</p>
                </button>
              );
            })}
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Asunto</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Organización</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Usuario</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Categoría</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Prioridad</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Estado</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actualización</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTickets.map((ticket) => {
                  const statusInfo = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
                  const priorityInfo = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.normal;
                  return (
                    <tr
                      key={ticket.id}
                      onClick={() => navigate(`/help/ticket/${ticket.id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-mono text-gray-400">
                        {ticket.ticketNumber}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                          {ticket.subject}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Building2 className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm text-gray-600 truncate max-w-[150px]">
                            {ticket.organization.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm text-gray-600 truncate max-w-[120px]">
                            {ticket.user.firstName} {ticket.user.lastName}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-500">
                          {CATEGORY_LABELS[ticket.category] || ticket.category}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityInfo.bg} ${priorityInfo.text}`}>
                          {priorityInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}>
                          {statusInfo.icon}
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                        {formatDate(ticket.updatedAt)}
                      </td>
                      <td className="px-4 py-3">
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
